import { useState, useMemo } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowsLeftRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function FindReplaceModal({ open, onOpenChange, texts, hasSelection, onReplaceAll }) {
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [selectedOnly, setSelectedOnly] = useState(false);

  const { matches, layers } = useMemo(() => {
    if (!find) return { matches: 0, layers: 0 };
    const esc = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(esc, matchCase ? "g" : "gi");
    let m = 0, l = 0;
    (texts || []).forEach((t) => {
      const found = (t.text || "").match(re);
      if (found) { m += found.length; l += 1; }
    });
    return { matches: m, layers: l };
  }, [find, matchCase, texts]);

  const doReplace = () => {
    const n = onReplaceAll(find, replace, { matchCase, selectedOnly });
    if (n > 0) { toast.success(`Replaced ${n} occurrence${n === 1 ? "" : "s"}`); onOpenChange(false); }
    else toast.error("No matches found");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-md" data-testid="find-replace-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <MagnifyingGlass size={20} weight="bold" className="text-blue-400" /> Find &amp; replace text
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Find</label>
            <Input autoFocus value={find} onChange={(e) => setFind(e.target.value)} placeholder="Text to find…" data-testid="find-input" className="mt-1.5 bg-black/40 border-white/15" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Replace with</label>
            <Input value={replace} onChange={(e) => setReplace(e.target.value)} placeholder="Replacement text…" data-testid="replace-input" className="mt-1.5 bg-black/40 border-white/15" />
          </div>

          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
              <input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} data-testid="match-case" className="accent-blue-500" /> Match case
            </label>
            {hasSelection && (
              <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                <input type="checkbox" checked={selectedOnly} onChange={(e) => setSelectedOnly(e.target.checked)} data-testid="selected-only" className="accent-blue-500" /> Selected layers only
              </label>
            )}
          </div>

          <div className="text-xs text-zinc-400" data-testid="match-count">
            {find ? (matches > 0 ? `${matches} match${matches === 1 ? "" : "es"} across ${layers} layer${layers === 1 ? "" : "s"}` : "No matches") : "Type something to find"}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="hover:bg-white/10">Cancel</Button>
          <Button onClick={doReplace} disabled={!find || matches === 0} data-testid="replace-all-btn" className="bg-blue-600 hover:bg-blue-500">
            <ArrowsLeftRight size={16} className="mr-1.5" /> Replace all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
